import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { devices, provisioning } from '../services/api';
import {
  WifiIcon,
  DevicePhoneMobileIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';

interface UnclaimedDevice {
  device_id: string;
  device_name: string;
  serial_number: string;
  mac_address: string;
  first_seen: string;
  last_seen: string | null;
  status: string;
}

const AddDevice: React.FC = () => {
  const { venueId } = useParams<{ venueId: string }>();
  const navigate = useNavigate();
  const [method, setMethod] = useState<'online' | 'new' | null>(null);
  const [deviceSerial, setDeviceSerial] = useState('');
  const [deviceLocation, setDeviceLocation] = useState('');
  const [searchingOnline, setSearchingOnline] = useState(false);
  const [unclaimedDevices, setUnclaimedDevices] = useState<UnclaimedDevice[]>([]);
  const [selectedDevice, setSelectedDevice] = useState<UnclaimedDevice | null>(null);
  const [claiming, setClaiming] = useState(false);
  const [success, setSuccess] = useState(false);

  // Search for devices already online
  const searchOnlineDevices = async () => {
    setSearchingOnline(true);
    try {
      const response = await provisioning.getUnclaimed();
      setUnclaimedDevices(response.data);
      if (response.data.length === 0) {
        alert('No unclaimed devices found on the network. Make sure your device is powered on and connected to the network.');
      }
    } catch (error) {
      console.error('Failed to search devices:', error);
      alert('Failed to search for devices');
    } finally {
      setSearchingOnline(false);
    }
  };

  // Claim an online device
  const handleClaimDevice = async () => {
    if (!selectedDevice || !deviceLocation) {
      alert('Please select a device and enter its location');
      return;
    }

    setClaiming(true);
    try {
      await provisioning.claim({
        deviceId: selectedDevice.device_id,
        serialNumber: selectedDevice.serial_number,
        venueId: venueId!,
        location: deviceLocation,
      });
      setSuccess(true);
      setTimeout(() => {
        navigate(`/venues/${venueId}`);
      }, 2000);
    } catch (error) {
      console.error('Failed to claim device:', error);
      alert('Failed to claim device');
      setClaiming(false);
    }
  };

  // Setup new device with WiFi
  const handleNewDeviceSetup = () => {
    // For now, redirect to the existing provisioning page
    // In production, this would guide through actual WiFi setup
    navigate(`/venues/${venueId}/provision-new`);
  };

  if (success) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-green-50 p-8 rounded-lg text-center">
          <CheckCircleIcon className="h-16 w-16 text-green-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-green-900 mb-2">Device Added Successfully!</h2>
          <p className="text-green-700">Redirecting to venue...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <Link
          to={`/venues/${venueId}`}
          className="text-indigo-600 hover:text-indigo-700 mb-4 inline-block"
        >
          ← Back to Venue
        </Link>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-gray-100">Add Device to Venue</h1>
      </div>

      {!method && (
        <div className="space-y-4">
          <h2 className="text-xl font-semibold mb-4">How would you like to add your device?</h2>
          
          <div 
            onClick={() => setMethod('online')}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-indigo-500"
          >
            <div className="flex items-start">
              <WifiIcon className="h-8 w-8 text-indigo-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold">Device is Already on Network</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Your device is already connected to WiFi and powered on. We'll search for it on the network.
                </p>
              </div>
            </div>
          </div>

          <div 
            onClick={() => setMethod('new')}
            className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow cursor-pointer hover:shadow-lg transition-shadow border-2 border-transparent hover:border-indigo-500"
          >
            <div className="flex items-start">
              <DevicePhoneMobileIcon className="h-8 w-8 text-indigo-600 mt-1" />
              <div className="ml-4">
                <h3 className="text-lg font-semibold">New Device (Needs WiFi Setup)</h3>
                <p className="text-gray-600 dark:text-gray-400 mt-1">
                  Your device is new or needs to be connected to WiFi. We'll guide you through the setup process.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {method === 'online' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Find Device on Network</h2>
          
          {!searchingOnline && unclaimedDevices.length === 0 && (
            <div className="space-y-4">
              <p className="text-gray-600 dark:text-gray-400">
                Make sure your AudioMap device is:
              </p>
              <ul className="list-disc list-inside text-gray-600 dark:text-gray-400 space-y-1 ml-4">
                <li>Powered on (LED should be blue or green)</li>
                <li>Connected to the same network as this computer</li>
                <li>Has been running for at least 1 minute</li>
              </ul>
              
              <button
                onClick={searchOnlineDevices}
                className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center justify-center"
              >
                <MagnifyingGlassIcon className="h-5 w-5 mr-2" />
                Search for Devices
              </button>
              
              <button
                onClick={() => setMethod(null)}
                className="w-full py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800"
              >
                Back
              </button>
            </div>
          )}

          {searchingOnline && (
            <div className="text-center py-8">
              <ArrowPathIcon className="h-8 w-8 text-indigo-600 animate-spin mx-auto mb-4" />
              <p className="text-gray-600 dark:text-gray-400">Searching for devices on the network...</p>
            </div>
          )}

          {!searchingOnline && unclaimedDevices.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-medium">Found {unclaimedDevices.length} unclaimed device(s):</h3>
              
              <div className="space-y-2">
                {unclaimedDevices.map(device => (
                  <div
                    key={device.device_id}
                    onClick={() => setSelectedDevice(device)}
                    className={`p-4 border rounded-lg cursor-pointer transition-colors ${
                      selectedDevice?.device_id === device.device_id
                        ? 'border-indigo-500 bg-indigo-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-medium">Serial: {device.serial_number}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">MAC: {device.mac_address}</p>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          First seen: {new Date(device.first_seen).toLocaleString()}
                        </p>
                      </div>
                      {selectedDevice?.device_id === device.device_id && (
                        <CheckCircleIcon className="h-5 w-5 text-indigo-600" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {selectedDevice && (
                <div className="space-y-4 pt-4 border-t">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Device Location/Name
                    </label>
                    <input
                      type="text"
                      value={deviceLocation}
                      onChange={(e) => setDeviceLocation(e.target.value)}
                      placeholder="e.g., Main Bar, Stage Left, Room 101"
                      className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:border-indigo-500"
                    />
                  </div>

                  <button
                    onClick={handleClaimDevice}
                    disabled={claiming || !deviceLocation}
                    className="w-full py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                  >
                    {claiming ? 'Adding Device...' : 'Add Device to Venue'}
                  </button>
                </div>
              )}

              <button
                onClick={() => {
                  setUnclaimedDevices([]);
                  setSelectedDevice(null);
                  setDeviceLocation('');
                }}
                className="w-full py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800"
              >
                Search Again
              </button>
            </div>
          )}
        </div>
      )}

      {method === 'new' && (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Setup New Device</h2>
          
          <div className="space-y-4">
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="font-medium text-blue-900 mb-2">WiFi Setup Mode</h3>
              <p className="text-blue-800 text-sm mb-3">
                To put your device into WiFi setup mode:
              </p>
              <ol className="list-decimal list-inside text-blue-800 text-sm space-y-1 ml-2">
                <li>Power on the AudioMap device</li>
                <li>Press and hold the button for 9+ seconds</li>
                <li>Release when LED flashes rapidly</li>
                <li>Device will create WiFi network: <strong>AudioDevice_Setup</strong></li>
              </ol>
            </div>

            <div className="border rounded-lg p-4">
              <h3 className="font-medium mb-2">Connect to Device WiFi</h3>
              <ol className="list-decimal list-inside text-gray-600 dark:text-gray-400 text-sm space-y-1">
                <li>On your phone/laptop, go to WiFi settings</li>
                <li>Connect to network: <strong>AudioDevice_Setup</strong></li>
                <li>Password: <strong>setupmode123</strong></li>
                <li>Open browser and go to: <strong>192.168.4.1</strong></li>
                <li>Select your venue's WiFi and enter password</li>
                <li>Click "Save & Connect"</li>
                <li>Device will restart and connect to your WiFi</li>
              </ol>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={() => setMethod('online')}
                className="flex-1 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                Device is Now Connected
              </button>
              <button
                onClick={() => setMethod(null)}
                className="flex-1 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-900"
              >
                Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddDevice;